import { cn } from "@/lib/utils";

export interface DateRangeValue {
  from?: string;
  to?: string;
}

export type DateRangePresetId =
  | "today"
  | "7d"
  | "30d"
  | "thisMonth"
  | "lastMonth"
  | "custom";

export const dateRangePresets: { id: DateRangePresetId; label: string }[] = [
  { id: "today", label: "Aujourd'hui" },
  { id: "7d", label: "7 jours" },
  { id: "30d", label: "30 jours" },
  { id: "thisMonth", label: "Ce mois" },
  { id: "lastMonth", label: "Mois dernier" },
];

const startOfDayIso = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
};

const endOfDayIso = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
};

export const computePresetRange = (preset: DateRangePresetId): DateRangeValue => {
  const now = new Date();
  switch (preset) {
    case "today":
      return { from: startOfDayIso(now), to: endOfDayIso(now) };
    case "7d": {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return { from: startOfDayIso(start), to: endOfDayIso(now) };
    }
    case "30d": {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return { from: startOfDayIso(start), to: endOfDayIso(now) };
    }
    case "thisMonth": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: startOfDayIso(start), to: endOfDayIso(now) };
    }
    case "lastMonth": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfDayIso(start), to: endOfDayIso(end) };
    }
    default:
      return {};
  }
};

/**
 * Returns the same-length window immediately preceding `range`.
 * Used for period-over-period comparisons.
 */
export const previousPeriodRange = (
  range: DateRangeValue
): DateRangeValue | null => {
  if (!range.from || !range.to) return null;
  const from = new Date(range.from);
  const to = new Date(range.to);
  const span = to.getTime() - from.getTime();
  if (span <= 0) return null;
  const prevTo = new Date(from.getTime() - 1);
  const prevFrom = new Date(prevTo.getTime() - span);
  return { from: prevFrom.toISOString(), to: prevTo.toISOString() };
};

interface DateRangeControlsProps {
  value: DateRangeValue;
  activePreset?: DateRangePresetId;
  onChange: (next: DateRangeValue, preset: DateRangePresetId) => void;
}

const DateRangeControls = ({
  value,
  activePreset = "custom",
  onChange,
}: DateRangeControlsProps) => (
  <div className="space-y-3">
    <div className="flex flex-wrap gap-1.5">
      {dateRangePresets.map((preset) => (
        <button
          key={preset.id}
          type="button"
          onClick={() => onChange(computePresetRange(preset.id), preset.id)}
          className={cn(
            "px-3 py-1.5 rounded-full border text-xs font-medium transition-colors",
            activePreset === preset.id
              ? "border-primary bg-primary/10 text-primary"
              : "border-border text-muted-foreground hover:border-primary/30"
          )}
        >
          {preset.label}
        </button>
      ))}
    </div>
    <div className="flex flex-col sm:flex-row gap-3">
      <div className="flex-1">
        <label
          htmlFor="range-from"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          Du
        </label>
        <input
          id="range-from"
          type="date"
          value={value.from ? value.from.slice(0, 10) : ""}
          onChange={(e) =>
            onChange(
              {
                ...value,
                from: e.target.value
                  ? `${e.target.value}T00:00:00.000Z`
                  : undefined,
              },
              "custom"
            )
          }
          className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>
      <div className="flex-1">
        <label
          htmlFor="range-to"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          Au
        </label>
        <input
          id="range-to"
          type="date"
          value={value.to ? value.to.slice(0, 10) : ""}
          onChange={(e) =>
            onChange(
              {
                ...value,
                to: e.target.value
                  ? `${e.target.value}T23:59:59.999Z`
                  : undefined,
              },
              "custom"
            )
          }
          className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>
    </div>
  </div>
);

export default DateRangeControls;
